import React from "react";

const linkClass = "text-[#BE3144] underline hover:text-[#9e2838] transition-colors";

const cardClass =
  "bg-white rounded-lg border border-gray-200 shadow-md p-8 scroll-mt-8";

function InShort({ children }) {
  return (
    <p className="text-sm text-gray-600 italic leading-relaxed mb-4 pl-3 border-l-2 border-[#BE3144]/40">
      <span className="not-italic font-semibold text-gray-800">In Short: </span>
      {children}
    </p>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="w-full h-screen overflow-y-scroll bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-8 pb-20">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Privacy Policy
          </h1>
          <p className="text-gray-500 mb-8">
            <b>Last updated on: February 12, 2024</b>
          </p>

          <div className={`${cardClass} mb-6`}>
            <p className="text-gray-700 leading-relaxed mb-4">
              This privacy notice for TryOn Labs / TryOn AI, operated by{" "}
              <strong className="text-gray-900">LEVIT8 LABS PRIVATE LIMITED</strong>{" "}
              (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;), describes how
              and why we might collect, store, use, and/or share (&quot;process&quot;)
              your information when you use our services (&quot;Services&quot;),
              such as when you:
            </p>
            <ul className="list-disc ml-6 space-y-2 text-gray-700 text-sm leading-relaxed mb-4">
              <li>
                Visit our website at tryonlabs.ai, or any website of ours that
                links to this privacy notice
              </li>
              <li>
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our Services.
              If you still have any questions or concerns, please contact us at{" "}
              <a href="mailto:contact@tryonlabs.ai" className={linkClass}>
                contact@tryonlabs.ai
              </a>
              . These Terms also apply together with our{" "}
              <a href="/terms-and-conditions" className={linkClass}>
                Terms &amp; Conditions
              </a>
              .
            </p>
          </div>

          <div className="space-y-6">
            <div className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Summary of Key Points
              </h2>
              <p className="text-gray-600 text-sm mb-6">
                This summary provides key points from our privacy notice. Use the
                table of contents below to find the section you are looking for.
              </p>
              <ul className="list-disc ml-6 space-y-3 text-gray-700 text-sm leading-relaxed">
                <li>
                  <strong className="text-gray-900">What personal information do we process?</strong>{" "}
                  When you visit, use, or navigate our Services, we may process
                  personal information depending on how you interact with us and
                  the Services, the choices you make, and the products and
                  features you use.
                </li>
                <li>
                  <strong className="text-gray-900">Do we process any sensitive personal information?</strong>{" "}
                  We may process sensitive personal information when necessary
                  with your consent or as otherwise permitted by applicable law.
                </li>
                <li>
                  <strong className="text-gray-900">Do we receive any information from third parties?</strong>{" "}
                  We do not receive any information from third parties.
                </li>
                <li>
                  <strong className="text-gray-900">How do we process your information?</strong>{" "}
                  We process your information to provide, improve, and administer
                  our Services, communicate with you, for security and fraud
                  prevention, and to comply with law. We may also process your
                  information for other purposes with your consent. We process
                  your information only when we have a valid legal reason to do so.
                </li>
                <li>
                  <strong className="text-gray-900">When do we share personal information?</strong>{" "}
                  We may share information in specific situations and with
                  specific third parties.
                </li>
                <li>
                  <strong className="text-gray-900">How do we keep your information safe?</strong>{" "}
                  We have organisational and technical processes and procedures
                  in place to protect your personal information. No electronic
                  transmission over the internet or information storage technology
                  can be guaranteed to be 100% secure.
                </li>
                <li>
                  <strong className="text-gray-900">What are your rights?</strong>{" "}
                  Depending on where you are located geographically, the
                  applicable privacy law may mean you have certain rights
                  regarding your personal information.
                </li>
                <li>
                  <strong className="text-gray-900">How do you exercise your rights?</strong>{" "}
                  The easiest way is by contacting us at{" "}
                  <a href="mailto:contact@tryonlabs.ai" className={linkClass}>
                    contact@tryonlabs.ai
                  </a>
                  . We will consider and act upon any request in accordance with
                  applicable data protection laws.
                </li>
              </ul>
            </div>

            <div className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Table of Contents
              </h2>
              <ol className="list-decimal ml-6 space-y-2 text-gray-700 text-sm leading-relaxed">
                <li><a href="#information-we-collect" className={linkClass}>What information do we collect?</a></li>
                <li><a href="#how-we-process" className={linkClass}>How do we process your information?</a></li>
                <li><a href="#legal-bases" className={linkClass}>What legal bases do we rely on?</a></li>
                <li><a href="#sharing" className={linkClass}>When and with whom do we share your personal information?</a></li>
                <li><a href="#retention" className={linkClass}>How long do we keep your information?</a></li>
                <li><a href="#security" className={linkClass}>How do we keep your information safe?</a></li>
                <li><a href="#minors" className={linkClass}>Do we collect information from minors?</a></li>
                <li><a href="#privacy-rights" className={linkClass}>What are your privacy rights?</a></li>
                <li><a href="#dnt" className={linkClass}>Controls for Do-Not-Track features</a></li>
                <li><a href="#us-residents" className={linkClass}>Do United States residents have specific privacy rights?</a></li>
                <li><a href="#other-regions" className={linkClass}>Do other regions have specific privacy rights?</a></li>
                <li><a href="#updates" className={linkClass}>Do we make updates to this notice?</a></li>
                <li><a href="#contact" className={linkClass}>How can you contact us about this notice?</a></li>
                <li><a href="#review-data" className={linkClass}>How can you review, update, or delete the data we collect from you?</a></li>
              </ol>
            </div>

            <div id="information-we-collect" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                1. What Information Do We Collect?
              </h2>
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                Personal information you disclose to us
              </h3>
              <InShort>
                We collect personal information that you provide to us.
              </InShort>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                We collect personal information that you voluntarily provide to
                us when you register on the Services, express an interest in
                obtaining information about us or our products and Services, when
                you participate in activities on the Services, or otherwise when
                you contact us.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed mb-3">
                <strong className="text-gray-900">Sensitive Information.</strong>{" "}
                When necessary, with your consent or as otherwise permitted by
                applicable law, we process the following categories of sensitive
                information:
              </p>
              <ul className="list-disc ml-6 space-y-2 text-gray-700 text-sm leading-relaxed mb-4">
                <li>Biometric data</li>
                <li>User uploaded photos</li>
              </ul>
              <p className="text-gray-700 text-sm leading-relaxed">
                All personal information that you provide to us must be true,
                complete, and accurate, and you must notify us of any changes to
                such personal information.
              </p>
            </div>

            <div id="how-we-process" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                2. How Do We Process Your Information?
              </h2>
              <InShort>
                We process your information to provide, improve, and administer
                our Services, communicate with you, for security and fraud
                prevention, and to comply with law. We may also process your
                information for other purposes with your consent.
              </InShort>
              <p className="text-gray-600 text-sm mb-4">
                We process your personal information for a variety of reasons,
                depending on how you interact with our Services, including:
              </p>
              <ul className="list-disc ml-6 space-y-3 text-gray-700 text-sm leading-relaxed">
                <li>
                  To facilitate account creation and authentication and otherwise
                  manage user accounts. We may process your information so you
                  can create and log in to your account, as well as keep your
                  account in working order.
                </li>
                <li>
                  To deliver and facilitate delivery of services to the user. We
                  may process your information to provide you with the requested
                  service.
                </li>
              </ul>
            </div>

            <div id="legal-bases" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                3. What Legal Bases Do We Rely On To Process Your Information?
              </h2>
              <InShort>
                We only process your personal information when we believe it is
                necessary and we have a valid legal reason (i.e. legal basis) to
                do so under applicable law, like with your consent, to comply
                with laws, to provide you with services to enter into or fulfil
                our contractual obligations, to protect your rights, or to
                fulfil our legitimate business interests.
              </InShort>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                If you are located in Canada, this section applies to you. We may
                process your information if you have given us specific permission
                (i.e. express consent) to use your personal information for a
                specific purpose, or in situations where your permission can be
                inferred (i.e. implied consent). You can withdraw your consent at
                any time.
              </p>
              <p className="text-gray-600 text-sm mb-3">
                In some exceptional cases, we may be legally permitted under
                applicable law to process your information without your consent,
                including, for example:
              </p>
              <ul className="list-disc ml-6 space-y-2 text-gray-700 text-sm leading-relaxed">
                <li>If collection is clearly in the interests of an individual and consent cannot be obtained in a timely way</li>
                <li>For investigations and fraud detection and prevention</li>
                <li>For business transactions provided certain conditions are met</li>
                <li>If it is contained in a witness statement and the collection is necessary to assess, process, or settle an insurance claim</li>
                <li>For identifying injured, ill, or deceased persons and communicating with next of kin</li>
                <li>If we have reasonable grounds to believe an individual has been, is, or may be victim of financial abuse</li>
                <li>If it is reasonable to expect collection and use with consent would compromise the availability or the accuracy of the information and the collection is reasonable for purposes related to investigating a breach of an agreement or a contravention of the laws of Canada or a province</li>
                <li>If disclosure is required to comply with a subpoena, warrant, court order, or rules of the court relating to the production of records</li>
                <li>If it was produced by an individual in the course of their employment, business, or profession and the collection is consistent with the purposes for which the information was produced</li>
                <li>If the collection is solely for journalistic, artistic, or literary purposes</li>
                <li>If the information is publicly available and is specified by the regulations</li>
              </ul>
            </div>

            <div id="sharing" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                4. When And With Whom Do We Share Your Personal Information?
              </h2>
              <InShort>
                We may share information in specific situations described in this
                section and/or with the following third parties.
              </InShort>
              <ul className="list-disc ml-6 space-y-3 text-gray-700 text-sm leading-relaxed">
                <li>
                  <strong className="text-gray-900">Business Transfers.</strong>{" "}
                  We may share or transfer your information in connection with,
                  or during negotiations of, any merger, sale of company assets,
                  financing, or acquisition of all or a portion of our business
                  to another company.
                </li>
              </ul>
            </div>

            <div id="retention" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                5. How Long Do We Keep Your Information?
              </h2>
              <InShort>
                We keep your information for as long as necessary to fulfil the
                purposes outlined in this privacy notice unless otherwise
                required by law.
              </InShort>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                We will only keep your personal information for as long as it is
                necessary for the purposes set out in this privacy notice, unless
                a longer retention period is required or permitted by law (such
                as tax, accounting, or other legal requirements). No purpose in
                this notice will require us keeping your personal information for
                longer than the period of time in which users have an account
                with us.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                When we have no ongoing legitimate business need to process your
                personal information, we will either delete or anonymise such
                information, or, if this is not possible (for example, because
                your personal information has been stored in backup archives),
                then we will securely store your personal information and isolate
                it from any further processing until deletion is possible.
              </p>
            </div>

            <div id="security" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                6. How Do We Keep Your Information Safe?
              </h2>
              <InShort>
                We aim to protect your personal information through a system of
                organisational and technical security measures.
              </InShort>
              <p className="text-gray-700 text-sm leading-relaxed">
                We have implemented appropriate and reasonable technical and
                organisational security measures designed to protect the
                security of any personal information we process. However, despite
                our safeguards and efforts to secure your information, no
                electronic transmission over the Internet or information storage
                technology can be guaranteed to be 100% secure, so we cannot
                promise or guarantee that hackers, cybercriminals, or other
                unauthorised third parties will not be able to defeat our
                security and improperly collect, access, steal, or modify your
                information. Although we will do our best to protect your
                personal information, transmission of personal information to and
                from our Services is at your own risk. You should only access the
                Services within a secure environment.
              </p>
            </div>

            <div id="minors" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                7. Do We Collect Information From Minors?
              </h2>
              <InShort>
                We do not knowingly collect data from or market to children under
                18 years of age.
              </InShort>
              <p className="text-gray-700 text-sm leading-relaxed">
                We do not knowingly solicit data from or market to children under
                18 years of age. By using the Services, you represent that you
                are at least 18 or that you are the parent or guardian of such a
                minor and consent to such minor dependent&apos;s use of the
                Services. If we learn that personal information from users less
                than 18 years of age has been collected, we will deactivate the
                account and take reasonable measures to promptly delete such data
                from our records. If you become aware of any data we may have
                collected from children under age 18, please contact us at{" "}
                <a href="mailto:contact@tryonlabs.ai" className={linkClass}>
                  contact@tryonlabs.ai
                </a>
                .
              </p>
            </div>

            <div id="privacy-rights" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                8. What Are Your Privacy Rights?
              </h2>
              <InShort>
                In some regions, such as the European Economic Area (EEA), United
                Kingdom (UK), Switzerland, and Canada, you have rights that allow
                you greater access to and control over your personal information.
              </InShort>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                In some regions (like the EEA, UK, Switzerland, and Canada), you
                have certain rights under applicable data protection laws. These
                may include the right (i) to request access and obtain a copy of
                your personal information, (ii) to request rectification or
                erasure; (iii) to restrict the processing of your personal
                information; (iv) if applicable, to data portability; and (v) not
                to be subject to automated decision-making. In certain
                circumstances, you may also have the right to object to the
                processing of your personal information. You can make such a
                request by contacting us using the contact details provided
                below.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                We will consider and act upon any request in accordance with
                applicable data protection laws. If you are located in the EEA or
                UK and you believe we are unlawfully processing your personal
                information, you also have the right to complain to your Member
                State data protection authority or UK data protection authority.
              </p>
            </div>

            <div id="dnt" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                9. Controls For Do-Not-Track Features
              </h2>
              <p className="text-gray-700 text-sm leading-relaxed">
                Most web browsers and some mobile operating systems and mobile
                applications include a Do-Not-Track (&quot;DNT&quot;) feature or
                setting you can activate to signal your privacy preference not to
                have data about your online browsing activities monitored and
                collected. At this stage, no uniform technology standard for
                recognising and implementing DNT signals has been finalised. As
                such, we do not currently respond to DNT browser signals or any
                other mechanism that automatically communicates your choice not
                to be tracked online. If a standard for online tracking is
                adopted that we must follow in the future, we will inform you
                about that practice in a revised version of this privacy notice.
              </p>
            </div>

            <div id="us-residents" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                10. Do United States Residents Have Specific Privacy Rights?
              </h2>
              <InShort>
                If you are a resident of California, Colorado, Connecticut, Utah,
                or Virginia, you may have additional rights regarding your
                personal information.
              </InShort>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                Depending on the state, these rights may include the right to be
                informed whether we are processing your personal data; to access,
                correct, or delete your personal data; to obtain a copy of the
                personal data you previously shared with us; and to opt out of
                processing used for targeted advertising, sale of personal data,
                or certain profiling.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                California residents also have rights under the CCPA/CPRA,
                including the right to request deletion, the right to know
                categories and specific pieces of personal information collected,
                the right to non-discrimination for exercising privacy rights, and
                the right to limit use and disclosure of sensitive personal
                information.
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                To exercise these rights, email{" "}
                <a href="mailto:contact@tryonlabs.ai" className={linkClass}>
                  contact@tryonlabs.ai
                </a>
                . We may need to verify your identity before processing a
                request. If we decline to take action and you wish to appeal,
                email us at the same address. Appeal timelines vary by state
                (typically 45–60 days). If you have a complaint about how we
                handle your data, we would like to hear from you.
              </p>
            </div>

            <div id="other-regions" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                11. Do Other Regions Have Specific Privacy Rights?
              </h2>
              <InShort>
                You may have additional rights based on the country you reside in.
              </InShort>
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                Australia and New Zealand
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                We collect and process your personal information under the
                obligations and conditions set by Australia&apos;s Privacy Act
                1988 and New Zealand&apos;s Privacy Act 2020. At any time, you
                have the right to request access to or correction of your
                personal information by contacting us using the details in
                section 14. If you believe we are unlawfully processing your
                personal information, you may submit a complaint to the Office of
                the Australian Information Commissioner or the Office of the New
                Zealand Privacy Commissioner.
              </p>
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                Republic of South Africa
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                You may request access to or correction of your personal
                information at any time. If you are unsatisfied with how we
                address a complaint, you can contact The Information Regulator
                (South Africa):{" "}
                <a href="mailto:enquiries@inforegulator.org.za" className={linkClass}>
                  enquiries@inforegulator.org.za
                </a>
                .
              </p>
            </div>

            <div id="updates" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                12. Do We Make Updates To This Notice?
              </h2>
              <InShort>
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws.
              </InShort>
              <p className="text-gray-700 text-sm leading-relaxed">
                We may update this privacy notice from time to time. The updated
                version will be indicated by an updated &quot;Revised&quot; date
                and the updated version will be effective as soon as it is
                accessible. If we make material changes to this privacy notice,
                we may notify you either by prominently posting a notice of such
                changes or by directly sending you a notification. We encourage
                you to review this privacy notice frequently to be informed of
                how we are protecting your information.
              </p>
            </div>

            <div id="contact" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                13. How Can You Contact Us About This Notice?
              </h2>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                If you have questions or comments about this notice, you may
                email us at{" "}
                <a href="mailto:contact@tryonlabs.ai" className={linkClass}>
                  contact@tryonlabs.ai
                </a>{" "}
                or contact us by post at:
              </p>
              <p className="text-gray-900 text-sm leading-relaxed">
                TryOn Labs / LEVIT8 LABS PRIVATE LIMITED
                <br />
                Bhive Platinum Workspace, HSR Sector 6
                <br />
                Bangalore, Karnataka 560102
                <br />
                India
              </p>
            </div>

            <div id="review-data" className={cardClass}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                14. How Can You Review, Update, Or Delete The Data We Collect From You?
              </h2>
              <p className="text-gray-700 text-sm leading-relaxed">
                You have the right to request access to the personal information
                we collect from you, change that information, or delete it. To
                request to review, update, or delete your personal information,
                please email{" "}
                <a href="mailto:contact@tryonlabs.ai" className={linkClass}>
                  contact@tryonlabs.ai
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
